import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../state/AuthContext';
import { IS_CAPACITOR } from '../api/config';
import { initPush, getPermissionStatus, requestAndRegister } from '../api/pushClient';
import { getNotificationPrefs } from '../api/authClient';
import NotificationOptInModal from './NotificationOptInModal';

// No UI of its own beyond the one-time opt-in prompt. Binds push listeners when
// signed in on iOS and surfaces the soft opt-in once the universe is ready.
function NotificationManager() {
  const { user, universeStatus } = useAuth();
  const [showOptIn, setShowOptIn] = useState(false);
  const initedForUser = useRef(null);

  // Once per login: bind listeners and refresh the device token if opted in.
  useEffect(() => {
    if (!IS_CAPACITOR || !user || initedForUser.current === user.id) return;
    initedForUser.current = user.id;
    let active = true;
    (async () => {
      await initPush();
      try {
        const res = await getNotificationPrefs();
        const data = await res.json();
        if (!active || !data.recommendation?.enabled) return;
        const status = await getPermissionStatus();
        if (status === 'granted') await requestAndRegister();
      } catch {
        /* ignore */
      }
    })();
    return () => { active = false; };
  }, [user]);

  // One-time soft opt-in once the universe first exists.
  useEffect(() => {
    if (!IS_CAPACITOR || !user || user.notificationPermissionPrompted) return;
    if (universeStatus === 'ready' || universeStatus === 'stale') {
      setShowOptIn(true);
    }
  }, [user, universeStatus]);

  if (!showOptIn) return null;
  return createPortal(
    <NotificationOptInModal onClose={() => setShowOptIn(false)} />,
    document.body
  );
}

export default NotificationManager;
